import type { CategoryAllocation, FinanceTransaction } from '@/types/finance';
import { distributeTotalWithCharges } from '@/lib/allocations';

/**
 * Retorna allocations efetivas de uma transação.
 * - Com split (categorySplits.length > 0): cada split é uma allocation.
 * - Sem split: 1 allocation virtual com (categoryId, amount).
 * - Sem categoria nem split: array vazio (não agrega no DRE).
 *
 * Helper duplicado em FinanceContext.getAllocations — mantemos cópia pura aqui
 * para os tests funcionarem sem dependência de React/contexto.
 */
export function getAllocationsFor(tx: FinanceTransaction): CategoryAllocation[] {
  if (tx.categorySplits && tx.categorySplits.length > 0) {
    return tx.categorySplits;
  }
  if (!tx.categoryId) return [];
  return [{ categoryId: tx.categoryId, amount: tx.amount, orderIndex: 0 }];
}

/**
 * Distribui o total efetivo (amount + interest + fine) proporcionalmente
 * entre as allocations da transação, com base no peso de cada allocation
 * em relação ao `amount` original.
 *
 * Exemplo: amount=R$5000, interest=R$50, fine=R$50, splits [{IRPJ,3000},{CSLL,2000}]
 * → total efetivo = R$5100. Ratio IRPJ = 3000/5000 = 0.6 → 5100 * 0.6 = R$3060
 * → Ratio CSLL = 0.4 → R$2040.
 *
 * Retorna um array de { categoryId, amount } onde amount é a parcela total
 * (incluindo encargos proporcionais).
 */
export function distributeWithCharges(
  tx: FinanceTransaction,
): { categoryId: string; amount: number }[] {
  return distributeTotalWithCharges(
    tx,
    getAllocationsFor(tx).map((a) => ({ key: a.categoryId, amount: a.amount })),
  ).map((p) => ({ categoryId: p.key, amount: p.amount }));
}
